'use client';

import { stegaClean } from '@sanity/client/stega';
import type { IPromoBanner } from '@/sanity/lib/definitions';
import { getBannerDesign } from '@/lib/bannerCountdown';
import PromoBannerDialogClassic from '@/components/global/PromoBannerDialogClassic';
import PromoBannerDialogV1 from '@/components/global/PromoBannerDialogV1';
import PromoBannerDialogHalloween from '@/components/global/PromoBannerDialogHalloween';
import PromoBannerDialogBlackMonth from '@/components/global/PromoBannerDialogBlackMonth';

export interface PromoBannerDialogProps {
  data: IPromoBanner;
  language?: string;
  /** Preview harnesses: open immediately, ignore the once-a-day stamp. */
  forceOpen?: boolean;
  /**
   * Partner code of an affiliate page (the `?aff=` of its hero CTA). Only
   * the Black Month design uses it: it shows the code box instead of the
   * countdown. The other designs ignore it.
   */
  affiliateCode?: string;
}

/**
 * Promo popup entry point. The document's Design field picks the layout:
 * every popup saved before the field existed has no value and keeps the
 * classic look; "New UI 2026" renders the dark glass design with the optional
 * countdown (components/global/PromoBannerDialogV1.tsx), and its seasonal
 * skins render the Halloween / Black Month dialogs.
 */
export default function PromoBannerDialog({ affiliateCode, ...props }: PromoBannerDialogProps) {
  const design = getBannerDesign({ design: stegaClean(props.data?.design) });
  switch (design) {
    case 'v1':
      return <PromoBannerDialogV1 {...props} />;
    case 'halloween':
      return <PromoBannerDialogHalloween {...props} affiliateCode={affiliateCode} />;
    case 'blackMonth':
      return <PromoBannerDialogBlackMonth {...props} affiliateCode={affiliateCode} />;
    default:
      return <PromoBannerDialogClassic {...props} />;
  }
}
